import React from 'react';

import { IconButton } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { FavoriteIcon } from 'components/favorite-icon';
import { toaster } from 'components/toaster/toaster';
import {
  GetUserFavoritesRefsDocument,
  ICartEntryFragment,
  ICartEntryInput,
  IGetUserFavoritesRefsQuery,
  useCreateFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetUserFavoritesRefsQuery,
} from 'generated/graphql-gateway';
import { useRoute } from 'hooks/navigation/use-route';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logButtonClick,
  logRBIEvent,
  logRBIProductsDetailsEvent,
} from 'state/amplitude';
import { ICartEntryLoggable } from 'state/amplitude/types';
import { useFavoritesContext } from 'state/favorites';
import { isOfferType } from 'state/order/utils';
import { remappedCartForBackEnd } from 'utils/cart';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';
import { getCartEntryRef } from 'utils/menu/get-cart-entry-ref';
import { routes } from 'utils/routing';

interface IFavoriteEntryButtonProps {
  cartEntry: ILegacyCartEntry;
  disabled?: boolean;
}

const FavoriteEntryButton: React.FC<React.PropsWithChildren<IFavoriteEntryButtonProps>> = ({
  cartEntry,
  disabled = false,
}) => {
  const { data } = useGetUserFavoritesRefsQuery();
  const [createFavorite, { loading: createLoading }] = useCreateFavoriteMutation();
  const [deleteFavorite, { loading: deleteLoading }] = useDeleteFavoriteMutation();

  const ref = getCartEntryRef(cartEntry);
  const refFavorite = data?.userFavorites.favorites?.find(favorite => favorite?.ref === ref);
  const isFavorite = !!refFavorite;
  const { formatMessage } = useIntl();
  const { pathname } = useRoute();
  const { setShouldRefetchFavorites } = useFavoritesContext();

  const shouldDisplayHeart = !isOfferType(cartEntry.type);

  const addEvent = (favoriteItemName: string) => {
    const isMenuCurrentPath = pathname.startsWith(routes.menu);

    logButtonClick({
      type: EventTypes.UserContent,
      attributes: {
        'Source Page': isMenuCurrentPath ? `${routes.menu}/` : pathname,
        Name: CustomEventNames.ADD_ITEM_TO_FAVORITES,
        name: CustomEventNames.ADD_ITEM_TO_FAVORITES,
        component: ClickEventComponentNames.BUTTON,
      },
    });

    logRBIEvent({
      name: CustomEventNames.ADD_ITEMS_TO_FAVORITE,
      type: EventTypes.Other,
      attributes: {
        productName: favoriteItemName,
      },
    });
  };

  const onClick = () => {
    if (isFavorite) {
      // Delete favorite
      deleteFavorite({
        variables: {
          favoriteId: refFavorite?._id ?? '',
        },
        update: (cache, { data: resultData }) => {
          const favs = cache.readQuery<IGetUserFavoritesRefsQuery>({
            query: GetUserFavoritesRefsDocument,
          });

          const newQuery = {
            userFavorites: {
              ...favs?.userFavorites,
              count: favs?.userFavorites.count ? favs.userFavorites.count - 1 : 0,
              favorites: favs?.userFavorites.favorites?.filter(
                item => item?._id !== resultData?.deleteFavorite._id
              ),
            },
          };
          const entries = resultData?.deleteFavorite?.entries ?? [];
          const products: ICartEntryFragment[] = entries
            .filter(entry => entry !== null)
            .map(entry => entry as ICartEntryLoggable);
          logRBIProductsDetailsEvent(
            {
              name: CustomEventNames.PRODUCT_FAVORITE_BUTTON_CLICKED,
              products,
              attributes: {
                status: 'Unfavorite',
              },
            },
            { skipLoggingToBraze: true }
          );
          cache.writeQuery({
            query: GetUserFavoritesRefsDocument,
            data: newQuery,
          });
        },
      })
        .then(() => {
          setShouldRefetchFavorites(true);
          toaster.positive(formatMessage({ id: 'removeFavoriteSucess' }));
        })
        .catch(() => {
          toaster.negative(formatMessage({ id: 'itemFavoritedError' }));
        });
    } else {
      // Map the entries to ICartEntryInput
      const entries = (remappedCartForBackEnd([cartEntry]) as unknown) as ICartEntryInput[];
      // Create favorite
      hapticImpact({ style: HapticsImpactStyle.Light });
      createFavorite({
        variables: {
          input: {
            entries,
            name: cartEntry.name,
            ref,
          },
        },
        update: (cache, { data: resultData }) => {
          const favs = cache.readQuery<IGetUserFavoritesRefsQuery>({
            query: GetUserFavoritesRefsDocument,
          });

          const newQuery = {
            userFavorites: {
              ...favs?.userFavorites,
              count: (favs?.userFavorites.count ?? 0) + 1,
              favorites: [
                {
                  ref: resultData?.createFavorite.ref ?? '',
                  _id: resultData?.createFavorite._id ?? '',
                  __typename: resultData?.createFavorite.__typename,
                },
              ].concat(
                favs?.userFavorites.favorites as ConcatArray<{
                  ref: string;
                  _id: string;
                  __typename: 'Favorite' | undefined;
                }>
              ),
            },
          };
          cache.writeQuery({
            query: GetUserFavoritesRefsDocument,
            data: newQuery,
          });
        },
      })
        .then(() => {
          addEvent(cartEntry.name);
          logRBIProductsDetailsEvent(
            {
              name: CustomEventNames.PRODUCT_FAVORITE_BUTTON_CLICKED,
              products: [cartEntry],
              attributes: {
                status: 'Favorite',
              },
            },
            { skipLoggingToBraze: true }
          );
          setShouldRefetchFavorites(true);
          toaster.positive(formatMessage({ id: 'itemFavorited' }));
        })
        .catch(() => {
          toaster.negative(formatMessage({ id: 'itemFavoritedError' }));
        });
    }
  };

  const loading = createLoading || deleteLoading;
  const accessibilityLabel = isFavorite
    ? formatMessage({ id: 'removeFavorite' })
    : formatMessage({ id: 'addFavorite' });

  return shouldDisplayHeart ? (
    <IconButton
      variant="ghost"
      onPress={onClick}
      testID="favorite-icon"
      disabled={disabled || loading}
      _web={{
        cursor: disabled ? 'not-allowed' : 'default',
      }}
      icon={
        <FavoriteIcon isFavorite={isFavorite} disabled={disabled || loading} loading={loading} />
      }
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ checked: true }}
    />
  ) : null;
};

export default FavoriteEntryButton;
